/**
 * candleFraction — "important fraction" candles.
 *
 * BYTE-IDENTICAL TWIN. The frontend copy is the source of truth:
 *     frontend/src/utils/candleFraction.ts
 *     backend/src/utils/candleFraction.ts
 * Guarded by `node scripts/check-interval-parity.mjs`. Fix a divergence with:
 *     cp frontend/src/utils/candleFraction.ts backend/src/utils/candleFraction.ts
 * Its only import is the (also twinned) intervalCatalog.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHAT IT DOES
 *
 * With the fraction set to 1/N, every chart bar is rebuilt from the LAST 1/N of
 * its own time before the chart's candle type (Reg / HA / CLH / CLN) is applied:
 *     O = the open of the first sub-bar inside the window
 *     H / L = the extremes inside the window (widened to the real close)
 *     C = the bar's REAL close (the window ends where the bar ends)
 *     V = the volume inside the window
 * Denominator 1 is today's chart, exactly: `applyFraction` hands back the SAME
 * array, so nothing downstream can tell the feature exists.
 *
 * DISPLAY ONLY (owner, 2026-09-30). Indicators, strategies, scans, alerts,
 * orders and P&L keep reading real bars. The Phase 0 study
 * (docs/FRACTION-CANDLES-FEASIBILITY.md) found no predictive edge in the last
 * slice of a bar, so nothing that DECIDES should ever read these bars.
 *
 * A fraction bar cannot be computed from the bar's own OHLC: it needs the bar's
 * inside. The server fetches finer bars ("sub-bars") and builds one row per
 * chart bar (`buildFractionRow`); the chart joins the rows on TIME.
 *
 * TWO WAYS TO MEASURE "THE LAST 1/N":
 *   clock   — 24x7 venues (Delta, synthetics). The window is the last 1/N of the
 *             bar's clock span, snapped to the sub-bar grid.
 *   session — everything else (NSE / MCX / CDS via Kite, GLOBAL via Yahoo). The
 *             window is the last 1/N of the bar's TRADING time, measured in the
 *             sub-bars that actually exist inside the bar, so a daily NSE bar's
 *             last third is the last 125 of its 375 session minutes, and a weekly
 *             bar's is the last third of that week's trading.
 */
import {
  VENUE_NATIVE, venueForExchange, isCalendarInterval, nextBucketStart, sessionOpenOffsetSec, DAY,
} from './intervalCatalog';

export interface FractionBar { time: number; open: number; high: number; low: number; close: number; volume?: number }

/** The denominators the setting offers. 1 = off. */
export const FRACTION_DENOMINATORS: readonly number[] = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

/** Above this many sub-bars per chart bar, an exact but heavy sub-bar loses to a snapped coarser one. */
export const MAX_SUBS_PER_BAR = 96;
/** The server fetches at most this many sub-bars per request; older bars stay normal. */
export const MAX_SUBS_PER_REQUEST = 40000;

export type FractionMode = 'clock' | 'session';

export interface FractionPlan {
  den: number;
  /** Sub-bar size in minutes. 0 when the fraction is off or unavailable. */
  subMin: number;
  mode: FractionMode;
  /** The nominal window divides into whole sub-bars (no snapping). */
  exact: boolean;
  /** Set when this timeframe cannot use the fraction; the chart then draws fraction 1 and says why. */
  unavailable?: string;
}

export interface FractionRow {
  /** The chart bar's open time (chart frame: IST-shifted seconds). */
  time: number;
  open: number; high: number; low: number; volume: number;
  /** Open time of the first sub-bar in the window. */
  windowStart: number;
  /** Sub-bars inside the window. */
  n: number;
}

/** 1 / off for anything that is not a whole number from 2 to 10. */
export function normaliseDenominator(v: unknown): number {
  const n = Math.round(Number(v));
  return Number.isFinite(n) && n >= 2 && n <= 10 ? n : 1;
}

/** '¹⁄₃'-style label for a denominator (empty for 1). */
export function fractionLabel(den: number): string {
  const d = normaliseDenominator(den);
  if (d === 1) return '';
  const SUB = ['₀', '₁', '₂', '₃', '₄', '₅', '₆', '₇', '₈', '₉'];
  return '¹⁄' + String(d).split('').map(ch => SUB[Number(ch)]).join('');
}

export function fractionMode(exchange: string | null | undefined): FractionMode {
  const e = (exchange || '').toUpperCase();
  return e === 'DELTA' || e === 'SYNTH' ? 'clock' : 'session';
}

/**
 * Nominal trading minutes per session day — used ONLY to choose a sub-bar size and
 * to place the forming bar's window. Closed bars measure the real trading time from
 * the sub-bars that exist, so holidays and short sessions come out right anyway.
 *   NSE / BSE / NFO / BFO 09:15-15:30 = 375 · MCX 09:00-23:30 = 870 (23:55 in US
 *   winter; nominal) · CDS 09:00-17:00 = 480 · GLOBAL (US equities) = 390.
 */
export function sessionMinutes(exchange: string | null | undefined): number {
  const e = (exchange || '').toUpperCase();
  if (e === 'MCX' || e === 'NCO') return 870;
  if (e === 'CDS') return 480;
  if (e === 'GLOBAL') return 390;
  return 375;
}

/** Trading minutes one bar of `intervalMin` spans, nominally. */
function nominalBarMinutes(exchange: string, intervalMin: number, mode: FractionMode): number {
  if (mode === 'clock') return isCalendarInterval(intervalMin) ? 30 * DAY : intervalMin;
  const s = sessionMinutes(exchange);
  if (isCalendarInterval(intervalMin)) return 21 * s;
  if (intervalMin >= DAY) {
    const days = intervalMin / DAY;
    // A week-multiple holds five sessions per week; day-multiples one per day.
    return intervalMin % (7 * DAY) === 0 ? (days / 7) * 5 * s : days * s;
  }
  return Math.min(intervalMin, s);
}

/**
 * Which sub-bar size to fetch. Prefer the COARSEST venue-native size that divides
 * the window exactly (no snapping) while staying under MAX_SUBS_PER_BAR; otherwise
 * the coarsest size no bigger than a quarter of the window (snapping by at most an
 * eighth of it); otherwise the finest the venue has.
 */
export function planFraction(exchange: string | null | undefined, intervalMin: number, den: number): FractionPlan {
  const d = normaliseDenominator(den);
  const mode = fractionMode(exchange);
  if (d === 1) return { den: 1, subMin: 0, mode, exact: true };
  const iv = Number(intervalMin);
  const ex = (exchange || '').toUpperCase();
  if (ex === 'SYNTH') {
    return { den: d, subMin: 0, mode, exact: false, unavailable: 'synthetic symbols are drawn at fraction 1' };
  }
  const L = nominalBarMinutes(ex, iv, mode);
  const W = L / d;
  const natives = VENUE_NATIVE[venueForExchange(ex)] || [];
  // A sub-bar must be smaller than the bar, and on a clock venue it must tile the
  // bar's grid (a 4H bar is 48 x 5m, never 16 x 15m-and-a-bit).
  const fits = (r: number): boolean => {
    if (r > W || r >= (isCalendarInterval(iv) ? 28 * DAY : iv)) return false;
    if (mode === 'session') return r < DAY;
    return isCalendarInterval(iv) || iv >= DAY ? DAY % r === 0 || r === DAY : iv % r === 0;
  };
  const cands = natives.filter(fits);
  if (!cands.length) {
    return {
      den: d, subMin: 0, mode, exact: false,
      unavailable: `the last ${fractionLabel(d)} of a bar is shorter than the smallest bar this venue serves`,
    };
  }
  const exact = cands.filter(r => Math.abs(W / r - Math.round(W / r)) < 1e-9 && L / r <= MAX_SUBS_PER_BAR);
  if (exact.length) return { den: d, subMin: Math.max(...exact), mode, exact: true };
  const snap = cands.filter(r => r <= W / 4);
  const subMin = snap.length ? Math.max(...snap) : Math.min(...cands);
  return { den: d, subMin, mode, exact: false };
}

/**
 * Where a clock-mode bar's window starts: the last 1/den of [start, end), snapped to
 * the sub-bar grid measured from the bar's own start, and never empty or the whole bar.
 */
export function clockWindowStart(start: number, end: number, den: number, subSec: number): number {
  const w = (end - start) / den;
  let ws = start + Math.round((end - w - start) / subSec) * subSec;
  if (ws >= end) ws = end - subSec;
  if (ws <= start && end - start > subSec) ws = start + subSec;
  return ws;
}

/**
 * One chart bar's fraction row from the sub-bars inside it. `subs` must be the
 * sub-bars with start <= time < end, ascending (times in the chart frame). Returns
 * null when no sub-bar falls in the window (thin data, or no history there): that
 * bar stays a normal candle and the chart fades it.
 */
export function buildFractionRow(
  span: { start: number; end: number }, subs: FractionBar[], plan: FractionPlan,
  opts: { windowStart?: number } = {},
): FractionRow | null {
  if (plan.den <= 1 || !plan.subMin || !subs.length) return null;
  let win: FractionBar[];
  if (opts.windowStart != null) {
    // The FORMING bar: its window was placed up front (formingWindowStart), because
    // counting the sub-bars traded so far would put a session bar's window too early.
    const ws = opts.windowStart;
    win = subs.filter(s => s.time >= ws && s.time < span.end);
  } else if (plan.mode === 'clock') {
    const ws = clockWindowStart(span.start, span.end, plan.den, plan.subMin * 60);
    win = subs.filter(s => s.time >= ws && s.time < span.end);
  } else {
    // Trading time: the last 1/den of the sub-bars that actually traded in the bar.
    const k = Math.max(1, Math.round(subs.length / plan.den));
    win = subs.slice(subs.length - k);
  }
  if (!win.length) return null;
  let hi = -Infinity, lo = Infinity, vol = 0;
  for (const s of win) {
    if (s.high > hi) hi = s.high;
    if (s.low < lo) lo = s.low;
    vol += Number(s.volume) || 0;
  }
  return { time: span.start, open: win[0].open, high: hi, low: lo, volume: vol, windowStart: win[0].time, n: win.length };
}

/**
 * A closed chart bar's span. The end is the bar's own bucket end, capped by the next
 * bar's start (a gap never stretches a bar), so a ragged last bucket or a 1M bar
 * comes out right. `offsetSec` is the venue's session-open offset.
 */
export function barSpan(time: number, nextTime: number | undefined, intervalMin: number, offsetSec: number): { start: number; end: number } {
  const own = isCalendarInterval(intervalMin) ? nextBucketStart(time, intervalMin, offsetSec) : time + intervalMin * 60;
  const end = nextTime != null && nextTime > time ? Math.min(own, nextTime) : own;
  return { start: time, end };
}

/** Spans for a run of chart bars, in order (the last one bounded only by its own bucket). */
export function barSpans(bars: FractionBar[], intervalMin: number, exchange: string | null | undefined): Array<{ start: number; end: number }> {
  const off = sessionOpenOffsetSec(exchange);
  const out: Array<{ start: number; end: number }> = new Array(bars.length);
  for (let i = 0; i < bars.length; i++) out[i] = barSpan(bars[i].time, bars[i + 1]?.time, intervalMin, off);
  return out;
}

/**
 * When the FORMING bar's window opens (chart-frame seconds), or null when it cannot
 * be known before the bar closes (session-venue weekly / monthly bars). Until then
 * the forming bar is drawn as a normal candle (owner, 2026-09-30).
 */
export function formingWindowStart(time: number, intervalMin: number, exchange: string | null | undefined, plan: FractionPlan): number | null {
  if (plan.den <= 1 || !plan.subMin) return null;
  const off = sessionOpenOffsetSec(exchange);
  const end = barSpan(time, undefined, intervalMin, off).end;
  if (plan.mode === 'clock') return clockWindowStart(time, end, plan.den, plan.subMin * 60);
  // Session venue. A weekly / monthly bar's remaining trading days are not known
  // up front, and a GLOBAL (US) session does not start at an IST offset this module
  // knows: those stay normal until they close.
  if (isCalendarInterval(intervalMin) || intervalMin > DAY || (exchange || '').toUpperCase() === 'GLOBAL') return null;
  // Session venue: the bar's trading part is [max(start, open), min(end, close)).
  const dayStart = time - (((time % 86400) + 86400) % 86400);
  const open = dayStart + off;
  const close = open + sessionMinutes(exchange) * 60;
  const s = Math.max(time, open), e = Math.min(end, close);
  if (e <= s) return null;
  // Same count the closed bar will use: the last round(tradedSubBars / den) sub-bars.
  const k = Math.max(1, Math.round(((e - s) / (plan.subMin * 60)) / plan.den));
  return e - k * plan.subMin * 60;
}

/** One bar with its fraction row applied: O/H/L/V from the window, the REAL close kept. */
export function applyFractionRow<T extends FractionBar>(bar: T, row: FractionRow): T {
  return {
    ...bar,
    open: row.open,
    high: Math.max(row.high, bar.close),
    low: Math.min(row.low, bar.close),
    volume: row.volume,
  };
}

/**
 * The chart's closed bars with fraction rows applied, joined on TIME. A bar without a
 * row keeps its real values and is listed in `missing` (the chart fades it).
 * Denominator 1 returns the SAME array and no misses: the bit-identical guarantee.
 */
export function applyFraction<T extends FractionBar>(
  bars: T[], rows: Map<number, FractionRow> | null | undefined, den: number,
): { bars: T[]; missing: Set<number> } {
  if (normaliseDenominator(den) === 1 || !rows) return { bars, missing: new Set<number>() };
  const missing = new Set<number>();
  const out = bars.map(b => {
    const r = rows.get(b.time);
    if (!r) { missing.add(b.time); return b; }
    return applyFractionRow(b, r);
  });
  return { bars: out, missing };
}

/**
 * The forming bar under a fraction. Before its window opens: the real bar,
 * unchanged. After: the fetched row's open and extremes, extended live by the
 * forming bar's close (a tick that makes a new extreme between refreshes is caught
 * through the close; the next refresh brings the window's true extremes).
 */
export function applyFractionLive<T extends FractionBar>(live: T, row: FractionRow | undefined, windowStart: number | null, nowSec: number): T {
  if (windowStart == null || nowSec < windowStart) return live;
  if (!row || row.time !== live.time) return live;
  return applyFractionRow(live, row);
}
